/**
 * Logging estructurado del front (GR_Linus: todo código entregado lleva logging
 * estructurado).
 *
 * Emite una línea JSON por evento con timestamp y ámbito.
 *
 * ---------------------------------------------------------------------------
 * US-66 (#201) — Soberanía de telemetría (R-PT-10, P1)
 * ---------------------------------------------------------------------------
 * Cualquier analítica/telemetría del front EXCLUYE el contenido de las
 * conversaciones del tenant: aquí solo viajan metadatos técnicos del propio
 * cliente (evento, ids, rol, timings, códigos de error, conteos), NUNCA texto
 * de mensajes, "de qué va", títulos ni nombres reales de chats.
 *
 * Hoy no hay ningún proveedor externo de telemetría: `emit` escribe a la
 * consola del navegador y nada más sale del cliente (ver
 * docs/soberania-telemetria-us66.md). Aun así, este módulo aplica un GUARD
 * defensivo: si un evento futuro intentara incluir un campo con contenido de
 * conversación, el guard lo ELIMINA antes de emitir y deja constancia del
 * nombre del campo descartado (solo el nombre, nunca el valor). Así el criterio
 * queda blindado por código + test aunque mañana se enchufe un sink externo.
 */

// Nombres de campo que transportan CONTENIDO de conversación del tenant y que,
// por tanto, nunca pueden viajar en un evento de telemetría (R-PT-10). Se
// comparan sin distinguir mayúsculas. La lista cubre el vocabulario del
// contrato del descriptor/chat/turno; ampliarla es barato y no rompe nada.
const CONTENT_KEYS = new Set([
  'texto',
  'mensaje',
  'mensaje_usuario',
  'respuesta',
  'markdown',
  'content',
  'contenido',
  'titulo',
  'de_que_va',
  'descripcion',
  'payload',
  'messages',
  'turnos',
  'query',
  'search',
  'busqueda',
].map(k => k.toLowerCase()))

// Recorre el objeto de datos y elimina cualquier clave de contenido de tenant
// (a cualquier profundidad, con tope de recursión para no colgarse ante ciclos
// o estructuras enormes). Devuelve { clean, dropped } sin mutar la entrada.
function scrubTenantContent(data, depth = 0) {
  const dropped = []
  if (data === null || typeof data !== 'object' || depth > 4) {
    return { clean: data, dropped }
  }
  if (Array.isArray(data)) {
    const clean = data.map(v => {
      const r = scrubTenantContent(v, depth + 1)
      dropped.push(...r.dropped)
      return r.clean
    })
    return { clean, dropped }
  }
  const clean = {}
  for (const [key, value] of Object.entries(data)) {
    if (CONTENT_KEYS.has(key.toLowerCase())) {
      dropped.push(key)
      continue
    }
    const r = scrubTenantContent(value, depth + 1)
    dropped.push(...r.dropped)
    clean[key] = r.clean
  }
  return { clean, dropped }
}

function emit(level, event, data = {}) {
  const { clean, dropped } = scrubTenantContent(data)
  const record = {
    ts: new Date().toISOString(),
    scope: 'portico',
    level,
    event,
    ...clean,
  }
  // R-PT-10: si el guard tuvo que descartar campos de contenido, se deja
  // constancia SOLO de los nombres (nunca los valores) para poder corregir el
  // origen sin filtrar nada del tenant.
  if (dropped.length) record._scrubbed = [...new Set(dropped)]
  const line = JSON.stringify(record)
  // eslint-disable-next-line no-console
  ;(console[level] ?? console.log)(line)
}

export const logEvent = (event, data) => emit('info', event, data)
export const logWarn  = (event, data) => emit('warn', event, data)
export const logError = (event, data) => emit('error', event, data)

// Exportado para test/lint del criterio de soberanía (US-66). No es API de uso
// general: valida que un objeto de datos no lleva contenido de conversación.
export { scrubTenantContent, CONTENT_KEYS }
